import React from "react";
import { Link } from "react-router-dom";
import { 
  ChevronRight, 
  Home, 
  Search,
  ArrowRight,
  Clock,
  CheckCircle,
  Leaf,
  Calculator,
  MapPin,
  Heart,
  Sun,
  Smile,
  Wind
} from "lucide-react";
import { Header, Footer } from "../../../../components/Layout";

const HolisticoPage = () => {
  const dimensions = [
    {
      title: "Física",
      description: "Nutrición, ejercicio, desintoxicación, sueño y cuidado del cuerpo.",
      practices: ["Yoga", "Ejercicio físico", "Nutrición terapéutica", "Masajes"],
      icon: Sun,
      color: "text-yellow-400"
    },
    {
      title: "Mental",
      description: "Pensamientos, creencias, patrones de pensamiento y claridad mental.",
      practices: ["Meditación", "Mindfulness", "Terapia cognitiva", "Psicoeducación"],
      icon: Wind,
      color: "text-blue-400"
    },
    {
      title: "Emocional",
      description: "Manejo de emociones, sanación de heridas y desarrollo de inteligencia emocional.",
      practices: ["Terapia expresiva", "Arte-terapia", "Trabajo en trauma", "Grupos de apoyo"],
      icon: Heart,
      color: "text-pink-400"
    },
    {
      title: "Espiritual",
      description: "Conexión con algo mayor, propósito de vida y valores personales.",
      practices: ["Meditación guiada", "Trabajo de sentido", "Conexión con naturaleza", "Prácticas contemplativas"],
      icon: Smile,
      color: "text-purple-400"
    }
  ];

  const practices = [
    "Yoga y movimiento consciente",
    "Meditación y mindfulness",
    "Terapia nutricional",
    "Arte-terapia y musicoterapia",
    "Conexión con la naturaleza",
    "Técnicas de respiración",
    "Acupuntura y medicina complementaria",
    "Trabajo con plantas medicinales (no psicoactivas)"
  ];

  const pageSchema = {
    "@context": "https://schema.org",
    "@type": "MedicalTherapy",
    "name": "Tratamiento Holístico para Adicciones",
    "description": "Enfoque integral que trata la adicción desde todas las dimensiones: física, mental, emocional y espiritual."
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Inicio", "item": "https://sinadicciones.org" },
      { "@type": "ListItem", "position": 2, "name": "Tratamientos", "item": "https://sinadicciones.org/tratamientos" },
      { "@type": "ListItem", "position": 3, "name": "Residencial", "item": "https://sinadicciones.org/tratamiento/residencial" },
      { "@type": "ListItem", "position": 4, "name": "Holístico", "item": "https://sinadicciones.org/tratamiento/residencial/holistico" }
    ]
  };

  return (
    <div className="min-h-screen bg-slate-950">
      
        <title>Tratamiento Holístico para Adicciones: Enfoque Integral | Sin Adicciones Chile</title>
        <meta name="description" content="Tratamiento holístico residencial para adicciones. Aborda cuerpo, mente, emociones y espíritu. Yoga, meditación, nutrición y terapias complementarias." />
        <link rel="canonical" href="https://sinadicciones.org/tratamiento/residencial/holistico" />
        <meta property="og:image" content="https://sinadicciones.org/og-image.png" />
        <meta property="og:image:width" content="1280" />
        <meta property="og:image:height" content="896" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sinadicciones.org/tratamiento/residencial/holistico" />
        <meta property="og:site_name" content="SinAdicciones.org" />
        <meta property="og:locale" content="es_CL" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://sinadicciones.org/og-image.png" />

        <script type="application/ld+json">{JSON.stringify(pageSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      

      <Header />
      <div className="h-16 md:h-20" />

      {/* Breadcrumb */}
      <div className="bg-slate-900 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <nav className="flex items-center gap-2 text-sm text-slate-400 flex-wrap">
            <Link to="/" className="hover:text-emerald-400 transition-colors flex items-center gap-1">
              <Home className="w-4 h-4" />
              Inicio
            </Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/tratamientos" className="hover:text-emerald-400 transition-colors">Tratamientos</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/tratamiento/residencial" className="hover:text-emerald-400 transition-colors">Residencial</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-slate-300">Holístico</span>
          </nav>
        </div>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-green-900/20 via-slate-900 to-slate-950" />
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-green-500/10 rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-16 md:py-20">
          <div className="max-w-4xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-14 h-14 rounded-2xl bg-green-500/20 flex items-center justify-center">
                <Leaf className="w-7 h-7 text-green-400" />
              </div>
              <div className="px-4 py-2 bg-green-500/10 border border-green-500/20 rounded-full">
                <span className="text-green-400 font-medium">Persona integral</span>
              </div>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Enfoque{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-400">
                Holístico
              </span>
            </h1>
            
            <p className="text-lg md:text-xl text-slate-300 mb-8 max-w-3xl">
              Trata la adicción desde todas las dimensiones del ser humano. 
              No solo el síntoma, sino la persona completa: cuerpo, mente, 
              emociones y espíritu.
            </p>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
              <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
                <Leaf className="w-5 h-5 text-green-400 mb-2" />
                <p className="text-white font-bold">4 dimensiones</p>
                <p className="text-slate-400 text-sm">de tratamiento</p>
              </div>
              <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
                <Clock className="w-5 h-5 text-green-400 mb-2" />
                <p className="text-white font-bold">3-12 meses</p>
                <p className="text-slate-400 text-sm">duración típica</p>
              </div>
              <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
                <Heart className="w-5 h-5 text-green-400 mb-2" />
                <p className="text-white font-bold">Integral</p>
                <p className="text-slate-400 text-sm">enfoque</p>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-4">
              <Link 
                to="/centros-de-rehabilitacion-drogas-chile?approach=holistico"
                className="px-6 py-3 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-xl transition-all flex items-center gap-2"
              >
                <Search className="w-5 h-5" />
                Centros con Enfoque Holístico
              </Link>
              <Link 
                to="/calculadora"
                className="px-6 py-3 bg-slate-800 hover:bg-slate-700 text-white font-semibold rounded-xl transition-all border border-slate-700 flex items-center gap-2"
              >
                <Calculator className="w-5 h-5" />
                Calcular Costo
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Four Dimensions */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Las Cuatro Dimensiones
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              El enfoque holístico reconoce que la adicción afecta y es afectada por todas las áreas de la vida.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {dimensions.map((dimension, index) => (
              <div 
                key={index}
                className="p-6 bg-slate-800/50 rounded-2xl border border-green-500/20"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
                    <dimension.icon className={`w-6 h-6 ${dimension.color}`} />
                  </div>
                  <h3 className={`text-xl font-bold ${dimension.color}`}>{dimension.title}</h3>
                </div>
                <p className="text-slate-400 mb-4">{dimension.description}</p>
                <div className="flex flex-wrap gap-2">
                  {dimension.practices.map((practice, idx) => (
                    <span key={idx} className="px-3 py-1 bg-slate-700/50 text-slate-300 text-sm rounded-full">
                      {practice}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Practices */}
      <section className="py-16 bg-slate-900/50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Prácticas Comunes
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {practices.map((practice, index) => (
              <div 
                key={index}
                className="flex items-center gap-3 p-4 bg-slate-800/50 rounded-xl border border-slate-700/50"
              >
                <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                <span className="text-slate-300">{practice}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Important Note */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-gradient-to-br from-yellow-900/30 to-slate-800/50 rounded-3xl p-8 border border-yellow-500/20">
            <h3 className="text-xl font-bold text-yellow-400 mb-4">Nota Importante</h3>
            <p className="text-slate-300 mb-4">
              El enfoque holístico es complementario, no sustituto del tratamiento basado en evidencia. 
              Los mejores centros combinan prácticas holísticas con terapia cognitivo-conductual y 
              atención médica profesional.
            </p>
            <p className="text-slate-400 text-sm">
              Desconfía de centros que solo ofrecen "sanación espiritual" sin psicólogos o psiquiatras 
              certificados. La adicción es una enfermedad que requiere atención profesional.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-slate-900/50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-green-900/50 to-slate-800/50 border border-green-500/20 p-8 md:p-12 text-center">
            <Leaf className="w-16 h-16 text-green-400 mx-auto mb-6" />
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Encuentra Centros con Enfoque Holístico
            </h2>
            <p className="text-slate-300 mb-8 max-w-2xl mx-auto">
              Busca centros que integren prácticas holísticas con tratamiento profesional.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              <Link 
                to="/centros-de-rehabilitacion-drogas-chile"
                className="px-8 py-4 bg-green-500 hover:bg-green-600 text-white font-bold rounded-xl transition-all flex items-center gap-2"
              >
                <MapPin className="w-5 h-5" />
                Ver Centros
              </Link>
              <Link 
                to="/tratamiento/residencial"
                className="px-8 py-4 bg-slate-800 hover:bg-slate-700 text-white font-semibold rounded-xl transition-all border border-slate-700 flex items-center gap-2"
              >
                <ArrowRight className="w-5 h-5" />
                Otros Enfoques
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-white mb-6">Otros Enfoques Terapéuticos</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { title: "Cognitivo", path: "/tratamiento/residencial/cognitivo", color: "text-purple-400" },
              { title: "Conductual", path: "/tratamiento/residencial/conductual", color: "text-orange-400" },
              { title: "Cognitivo-Conductual", path: "/tratamiento/residencial/cognitivo-conductual", color: "text-emerald-400" },
              { title: "Integrativo", path: "/tratamiento/residencial/integrativo", color: "text-pink-400" }
            ].map((item, index) => (
              <Link 
                key={index}
                to={item.path}
                className="flex items-center gap-3 p-4 bg-slate-800/50 rounded-xl border border-slate-700/50 hover:border-green-500/30 transition-all group"
              >
                <span className={`font-medium ${item.color}`}>{item.title}</span>
                <ArrowRight className="w-4 h-4 text-slate-500 ml-auto group-hover:translate-x-1 transition-transform" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HolisticoPage;
